import type { GenerationParameters, Model } from "@lumen/shared";

export const generationParameterFields: {
  key: Exclude<
    keyof GenerationParameters,
    | "activity"
    | "voice"
    | "format"
    | "goal"
    | "activityModelIds"
    | "enabledActivities"
  >;
  providerKeys: string[];
  label: string;
  min: number;
  max: number;
  step: number;
  placeholder: string;
}[] = [
  {
    key: "temperature",
    providerKeys: ["temperature"],
    label: "Temperature",
    min: 0,
    max: 2,
    step: 0.1,
    placeholder: "Default",
  },
  {
    key: "topP",
    providerKeys: ["top_p"],
    label: "Top P",
    min: 0,
    max: 1,
    step: 0.05,
    placeholder: "Default",
  },
  {
    key: "maxTokens",
    providerKeys: ["max_tokens", "max_completion_tokens"],
    label: "Max output tokens",
    min: 1,
    max: 1000000,
    step: 1,
    placeholder: "Default",
  },
  {
    key: "frequencyPenalty",
    providerKeys: ["frequency_penalty"],
    label: "Frequency penalty",
    min: -2,
    max: 2,
    step: 0.1,
    placeholder: "Default",
  },
  {
    key: "presencePenalty",
    providerKeys: ["presence_penalty"],
    label: "Presence penalty",
    min: -2,
    max: 2,
    step: 0.1,
    placeholder: "Default",
  },
  {
    key: "seed",
    providerKeys: ["seed", "random_seed"],
    label: "Seed",
    min: -2147483648,
    max: 2147483647,
    step: 1,
    placeholder: "Random",
  },
];

export const TRIPO_RIGGING_MODEL_ID = "rig-v2.0";
export const TRIPO_ANIMATION_MODEL_ID = "tripo-animation";

export function scenarioInputDefinitions(model: Model | undefined) {
  if (!model?.remoteMetadata || typeof model.remoteMetadata !== "object")
    return [];
  const metadata = model.remoteMetadata as Record<string, unknown>;
  for (const candidate of [
    metadata.inputs,
    metadata.input_schema,
    metadata.inputSchema,
  ]) {
    if (!Array.isArray(candidate)) continue;
    return candidate.filter((input): input is Record<string, unknown> =>
      Boolean(
        input && typeof input === "object" && typeof input.name === "string",
      ),
    );
  }
  return [];
}

export function waveSpeedInputDefinitions(model: Model | undefined) {
  if (!model?.remoteMetadata || typeof model.remoteMetadata !== "object")
    return [];
  const metadata = model.remoteMetadata as Record<string, unknown>;
  const apiSchema = metadata.api_schema ?? metadata.apiSchema;
  if (!apiSchema || typeof apiSchema !== "object") return [];
  const schemas =
    (apiSchema as Record<string, unknown>).api_schemas ??
    (apiSchema as Record<string, unknown>).apiSchemas;
  if (!Array.isArray(schemas)) return [];
  const runSchema = schemas.find(
    (schema) =>
      schema &&
      typeof schema === "object" &&
      ((schema as Record<string, unknown>).type === "model_run" ||
        (schema as Record<string, unknown>).method === "POST"),
  );
  const requestSchema =
    runSchema && typeof runSchema === "object"
      ? ((runSchema as Record<string, unknown>).request_schema ??
        (runSchema as Record<string, unknown>).requestSchema)
      : undefined;
  const properties =
    requestSchema && typeof requestSchema === "object"
      ? (requestSchema as Record<string, unknown>).properties
      : undefined;
  if (
    !properties ||
    typeof properties !== "object" ||
    Array.isArray(properties)
  )
    return [];
  return Object.entries(properties as Record<string, unknown>).map(
    ([name, value]) => ({
      name,
      schema:
        value && typeof value === "object"
          ? (value as Record<string, unknown>)
          : {},
    }),
  );
}
